import React, { useEffect, useState, useRef } from "react";
import {
  VStack,
  Button,
  Box,
  Text,
  Divider,
  Icon,
  useColorModeValue,
  HStack,
  IconButton,
  Flex,
  Tooltip,
  Spinner,
  Alert,
  AlertIcon,
  useBreakpointValue,
  SimpleGrid,
  Center,
  Heading,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BsCalendarCheck,
  BsClockHistory,
  BsCreditCard,
  BsShieldCheck,
  BsStickies,
  BsGear,
  BsPeople,
  BsPersonBadge,
  BsClipboardCheck,
  BsCalendarEvent,
  BsCashStack,
  BsGraphUp,
  BsPersonCheck,
  BsMortarboard,
  BsReceipt,
  BsBoxArrowRight,
  BsBalance,
  BsBarChart,
  BsPlug,
  BsRocketTakeoff,
  BsPhone,
  BsChatDots,
  BsUiChecksGrid,
  BsBank,
  BsFileEarmarkText,
  BsSignpost2,
  BsCreditCard2Back,
  BsChevronLeft,
  BsChevronRight,
  BsCart,
  BsBoxSeam,
  BsCheckCircle,
  BsArrowLeft,
} from "react-icons/bs";
import { getSidebarModules } from "../services/sidebarService";
import { useAuth } from "../context/AuthContext";

// Role-based access control configuration
const ROLE_PERMISSIONS = {
  SuperAdmin: ["*"], // Full access
  Admin: ["*"], // Full access
  HRHead: [
    "core-hr",
    "recruiting",
    "attendance",
    "leave",
    "payroll",
    "performance",
    "ess",
    "lnd",
    "exit",
    "compliance",
    "analytics",
    "procurement",
  ],
  HRExecutive: [
    "core-hr",
    "recruiting",
    "attendance",
    "leave",
    "ess",
    "lnd",
    "exit",
    "analytics",
    "procurement",
  ],
  Manager: [
    "core-hr",
    "attendance",
    "leave",
    "performance",
    "ess",
    "analytics",
    "procurement",
  ],
  TeamLead: [
    "core-hr",
    "attendance",
    "leave",
    "ess",
    "analytics",
    "procurement",
  ],
  Employee: ["ess", "attendance", "leave"],
  Finance: ["payroll", "expense-travel", "analytics", "ess", "procurement"],
  ITAdmin: ["core-hr", "tech", "analytics", "ticketing"],
  ITSupport: ["tech", "ticketing"],
  Auditor: ["analytics", "compliance", "payroll", "procurement"],
};

// Helper function to check if user has access to a module
const hasModuleAccess = (userRole, modulePath) => {
  // If no role is set, show all items (backward compatibility)
  if (!userRole) {
    console.warn("[Sidebar] No user role found, showing all items");
    return true;
  }

  // Try exact match first, then case-insensitive match
  let rolePerms = ROLE_PERMISSIONS[userRole];
  if (!rolePerms) {
    const matchedRole = Object.keys(ROLE_PERMISSIONS).find(
      (key) => key.toLowerCase() === userRole.toLowerCase(),
    );
    rolePerms = matchedRole ? ROLE_PERMISSIONS[matchedRole] : null;
  }

  // If role not found in permissions, show all items (backward compatibility)
  if (!rolePerms) {
    console.warn(
      `[Sidebar] Role "${userRole}" not found in ROLE_PERMISSIONS, showing all items`,
    );
    return true;
  }

  // SuperAdmin and Admin have full access
  if (rolePerms.includes("*")) return true;

  // Extract module key from path (e.g., "/core-hr/employee" -> "core-hr")
  const moduleKey = modulePath.split("/")[1];

  return rolePerms.includes(moduleKey);
};

// Icon mapper - maps icon name strings to actual icon components
const ICON_MAPPER = {
  BsPeople,
  BsFileEarmarkText,
  BsPersonBadge,
  BsUiChecksGrid,
  BsGear,
  BsClipboardCheck,
  BsPlug,
  BsGraphUp,
  BsSignpost2,
  BsCalendarEvent,
  BsShieldCheck,
  BsCalendarCheck,
  BsClockHistory,
  BsCashStack,
  BsCreditCard,
  BsCreditCard2Back,
  BsBoxArrowRight,
  BsBank,
  BsReceipt,
  BsChatDots,
  BsMortarboard,
  BsPhone,
  BsBarChart,
  BsRocketTakeoff,
  BsStickies,
  BsCart,
  BsBoxSeam,
  BsCheckCircle,
};

// Fallback icon for biometric (no BsDeviceBiometric in some icon packs)
function BsDeviceBiometric(props) {
  return <BsShieldCheck {...props} />;
}
const STORAGE_KEY = "sidebar:collapsed";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user } = useAuth(); // Get token and user from AuthContext

  // Breakpoint detection for mobile
  const isMobile = useBreakpointValue(
    { base: true, md: false },
    { fallback: "md" },
  );

  // Check if we're on home/root path (show sidebar) or on a page (hide sidebar on mobile)
  const isHomePath = location.pathname === "/" || location.pathname === "";

  // State to track if sidebar was explicitly closed via back button
  const [sidebarClosed, setSidebarClosed] = useState(false);

  const shouldShowMobileSidebar = isMobile && isHomePath && !sidebarClosed;

  // State for modules from backend
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs for auto-scroll
  const scrollContainerRef = useRef(null);
  const activeItemRef = useRef(null);

  // Get user role from user object or localStorage as fallback
  const [userRole, setUserRole] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      // First try from user object
      if (user?.role) return user.role;

      // Fallback to localStorage
      const companyData = JSON.parse(localStorage.getItem("company") || "{}");
      const role = companyData.role || null;
      console.log("[Sidebar] User role loaded:", role);
      return role;
    } catch (error) {
      console.error("[Sidebar] Error reading user role:", error);
      return null;
    }
  });

  // Update userRole when user changes
  useEffect(() => {
    if (user?.role) {
      setUserRole(user.role);
    }
  }, [user]);

  // Auto-scroll to active item
  useEffect(() => {
    if (activeItemRef.current && scrollContainerRef.current && !loading) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        activeItemRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [location.pathname, loading, modules]); // Scroll when location or modules change

  // Reset sidebar closed flag when navigating away from home
  useEffect(() => {
    if (!isHomePath) {
      setSidebarClosed(false);
    }
  }, [isHomePath]);

  // 1) Read once from localStorage (SSR-safe)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) === true : false;
    } catch {
      return false;
    }
  });

  // Auto-collapse on mobile
  useEffect(() => {
    if (isMobile && !collapsed) {
      setCollapsed(true);
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(true));
    } else if (
      !isMobile &&
      collapsed &&
      localStorage.getItem(STORAGE_KEY) === "true"
    ) {
      // Restore collapsed state from localStorage when returning to desktop
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "false");
      if (!stored) {
        setCollapsed(false);
      }
    }
  }, [isMobile]);

  // Fetch sidebar modules from backend
  useEffect(() => {
    const fetchModules = async () => {
      console.log("[Sidebar] Auth check:", {
        hasToken: !!token,
        userRole,
        isAuthenticated: !!token,
      });

      if (!token) {
        console.log("[Sidebar] No auth token available, skipping API call");
        setLoading(false);
        setModules([]);
        setError(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getSidebarModules();

        // If no data, set empty
        if (!data || data.length === 0) {
          console.log("[Sidebar] No modules data received");
          setModules([]);
          setLoading(false);
          return;
        }

        // Map icon strings to actual icon components
        const modulesWithIcons = data.map((module) => ({
          ...module,
          items: module.items.map((item) => ({
            ...item,
            icon: ICON_MAPPER[item.icon] || BsStickies, // Fallback icon
          })),
        }));

        setModules(modulesWithIcons);
        console.log(
          "[Sidebar] Modules loaded from backend:",
          modulesWithIcons.length,
        );
      } catch (err) {
        console.error("[Sidebar] Error loading modules:", err);
        // Don't show error for authentication issues
        if (err.message === "Not authenticated") {
          setError(null);
        } else {
          setError("Failed to load sidebar configuration");
        }
        setModules([]);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, [token, userRole]); // Re-fetch when token or userRole changes

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
    } catch {}
  }, [collapsed]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue != null) {
        try {
          const next = JSON.parse(e.newValue);
          if (typeof next === "boolean" && next !== collapsed)
            setCollapsed(next);
        } catch {}
      }
      // Update user role if company data changes
      if (e.key === "company" && e.newValue != null) {
        try {
          const companyData = JSON.parse(e.newValue);
          const newRole = companyData.role || null;
          console.log("[Sidebar] Role updated from storage event:", newRole);
          setUserRole(newRole);
        } catch (error) {
          console.error("[Sidebar] Error updating role from storage:", error);
        }
      }
    };

    // Listen for custom login event (same tab)
    const onLogin = () => {
      console.log("[Sidebar] Login event detected, refreshing user role");
      try {
        const companyData = JSON.parse(localStorage.getItem("company") || "{}");
        const newRole = companyData.role || null;
        setUserRole(newRole);
      } catch (error) {
        console.error("[Sidebar] Error reading role after login:", error);
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("user-login", onLogin);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("user-login", onLogin);
    };
  }, [collapsed]);

  const bg = useColorModeValue("gray.50", "gray.900");
  const borderClr = useColorModeValue("gray.200", "gray.700");
  const activeBg = useColorModeValue("teal.100", "teal.600");
  const activeColor = useColorModeValue("teal.700", "white");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const muted = useColorModeValue("gray.500", "gray.500");
  const hoverBg = useColorModeValue("gray.200", "gray.700");

  const isActive = (to) =>
    location.pathname === to || location.pathname.startsWith(to + "/");

  // Mobile card tile component - for grid view
  const MobileCardTile = ({ item }) => (
    <Button
      as={Link}
      to={item.to}
      h="auto"
      p={0}
      variant="unstyled"
      display="flex"
      flexDirection="column"
      gap={2}
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      _hover={{
        transform: "translateY(-4px)",
        transition: "transform 0.2s ease",
      }}
      ref={isActive(item.to) ? activeItemRef : null}
    >
      <Box
        w="80px"
        h="80px"
        borderRadius="lg"
        bg={
          isActive(item.to)
            ? activeBg
            : useColorModeValue("gray.100", "gray.700")
        }
        display="flex"
        alignItems="center"
        justifyContent="center"
        transition="all 0.2s ease"
        border={isActive(item.to) ? "2px solid" : "none"}
        borderColor={isActive(item.to) ? activeColor : "transparent"}
      >
        <Icon
          as={item.icon}
          boxSize={10}
          color={isActive(item.to) ? activeColor : textColor}
        />
      </Box>
      <Text
        fontSize="sm"
        fontWeight="medium"
        textAlign="center"
        color={textColor}
        maxW="80px"
        isTruncated
      >
        {item.label}
      </Text>
    </Button>
  );

  return (
    <>
      {/* Mobile Layout - Full Page Card Grid (only on home) */}
      {shouldShowMobileSidebar && (
        <Box
          position="fixed"
          top={16}
          left={0}
          right={0}
          bottom={0}
          bg={bg}
          display={{ base: "flex", md: "none" }}
          flexDirection="column"
          zIndex={20}
          overflowY="auto"
          py={6}
          px={4}
        >
          {loading ? (
            <Flex justify="center" align="center" h="100%">
              <Spinner size="lg" color="teal.500" />
            </Flex>
          ) : error ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          ) : modules.length === 0 ? (
            <Flex justify="center" align="center" h="100%">
              <Text fontSize="sm" color={muted}>
                Please log in to view menu
              </Text>
            </Flex>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <Flex justify="space-between" align="center">
                <Box>
                  <Heading size="lg" mb={2}>
                    Navigation
                  </Heading>
                  <Text fontSize="sm" color={muted}>
                    Access all modules
                  </Text>
                </Box>
                <IconButton
                  icon={<BsArrowLeft />}
                  onClick={() => {
                    setSidebarClosed(true);
                    navigate(-1);
                  }}
                  variant="ghost"
                  size="sm"
                  aria-label="Go back"
                  title="Go back"
                  _hover={{ bg: hoverBg }}
                />
              </Flex>

              {/* Category Sections */}
              {modules.map((section) => {
                const accessibleItems = section.items.filter((item) =>
                  hasModuleAccess(userRole, item.to),
                );

                if (accessibleItems.length === 0) return null;

                return (
                  <Box key={section.heading}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      textTransform="uppercase"
                      mb={4}
                      px={1}
                      letterSpacing="0.04em"
                      color={muted}
                    >
                      {section.heading}
                    </Text>
                    <SimpleGrid columns={3} spacing={4}>
                      {accessibleItems.map((item) => (
                        <MobileCardTile key={item.to} item={item} />
                      ))}
                    </SimpleGrid>
                  </Box>
                );
              })}

              {/* Footer spacing */}
              <Box h={4} />
            </VStack>
          )}
        </Box>
      )}

      {/* Desktop Layout - Left Sidebar */}
      <Flex
        position="fixed"
        top={16}
        left={0}
        bottom={0}
        w={collapsed ? "64px" : "240px"}
        bg={bg}
        borderRight="1px solid"
        borderColor={borderClr}
        h="calc(100dvh - 12px)"
        boxShadow="sm"
        transition="width 0.2s ease"
        direction="column"
        display={{ base: "none", md: "flex" }}
      >
        {/* Floating edge toggle */}
        <IconButton
          aria-label="Toggle sidebar"
          icon={collapsed ? <BsChevronRight /> : <BsChevronLeft />}
          size="sm"
          variant="solid"
          colorScheme="gray"
          position="absolute"
          top="50%"
          right={collapsed ? "-12px" : "-14px"}
          transform="translateY(-50%)"
          zIndex={2}
          borderRadius="full"
          onClick={() => {
            const next = !collapsed;
            setCollapsed(next);
            window.localStorage.setItem(
              "sidebar:collapsed",
              JSON.stringify(next),
            );
            window.dispatchEvent(new Event("sidebar-toggle")); // notify same tab
          }}
          aria-expanded={!collapsed}
          boxShadow="md"
        />

        {/* Scrollable content */}
        <Box
          ref={scrollContainerRef}
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          px={collapsed ? 1 : 3}
          py={3}
        >
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="md" color="teal.500" />
            </Flex>
          ) : error ? (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              {!collapsed && error}
            </Alert>
          ) : modules.length === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column">
              {!collapsed && (
                <Text fontSize="sm" color={muted} textAlign="center" px={4}>
                  Please log in to view menu
                </Text>
              )}
            </Flex>
          ) : (
            <VStack spacing={4} align="stretch">
              {modules.map((section, i) => {
                // Filter items based on user role
                const accessibleItems = section.items.filter((item) =>
                  hasModuleAccess(userRole, item.to),
                );

                // Skip section if no accessible items
                if (accessibleItems.length === 0) return null;

                return (
                  <Box key={section.heading}>
                    {!collapsed ? (
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        textTransform="uppercase"
                        // color={muted}
                        mb={2}
                        px={1}
                        letterSpacing="0.04em"
                      >
                        {section.heading}
                      </Text>
                    ) : (
                      // thin separators between groups in collapsed mode
                      i !== 0 && <Divider my={2} borderColor={borderClr} />
                    )}

                    <VStack spacing={1} align="stretch">
                      {accessibleItems.map((item) => (
                        <Tooltip
                          key={item.to}
                          label={collapsed ? item.label : undefined}
                          placement="right"
                          openDelay={200}
                          hasArrow
                        >
                          <Button
                            ref={isActive(item.to) ? activeItemRef : null}
                            as={Link}
                            to={item.to}
                            justifyContent={collapsed ? "center" : "flex-start"}
                            leftIcon={<Icon as={item.icon} boxSize={4} />}
                            variant="ghost"
                            fontWeight="medium"
                            bg={isActive(item.to) ? activeBg : "transparent"}
                            color={isActive(item.to) ? activeColor : textColor}
                            _hover={{ bg: hoverBg }}
                            size="sm"
                            borderRadius="md"
                            px={collapsed ? 0 : 2}
                          >
                            {!collapsed && item.label}
                          </Button>
                        </Tooltip>
                      ))}
                    </VStack>
                  </Box>
                );
              })}
            </VStack>
          )}

          {!collapsed && !loading && !error && (
            <>
              <Divider my={6} borderColor={borderClr} />
              <Text px={1} fontSize="xs" color={muted}>
                © {new Date().getFullYear()} NoraHuman
              </Text>
            </>
          )}
        </Box>
      </Flex>
    </>
  );
};

export default Sidebar;
